import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, FileSpreadsheet, Download, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ENTITY_LABELS = {
  clients: 'Clients', groups: 'Groups', centers: 'Centers', offices: 'Offices',
  employees: 'Employees', loans: 'Loan Accounts', savings: 'Savings Accounts',
  journal_entries: 'Journal Entries', chart_of_accounts: 'Chart of Accounts', share_accounts: 'Share Accounts',
};

const BulkImportPage = () => {
  const [entity, setEntity] = useState('');
  const [file, setFile] = useState(null);
  const [templates, setTemplates] = useState({});
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get('/bulk-import/templates').then(r => setTemplates(r.data || {})).catch(() => {});
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f);
    setResults(null);
    if (f) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target.result;
        const lines = text.split('\n').filter(l => l.trim());
        const headers = lines[0]?.split(',').map(h => h.trim().replace(/"/g, ''));
        const rows = lines.slice(1, 6).map(l => l.split(',').map(c => c.trim().replace(/"/g, '')));
        setPreview({ headers, rows, totalRows: lines.length - 1 });
      };
      reader.readAsText(f);
    } else {
      setPreview(null);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!entity) { toast.error('Select entity type first'); return; }
    try {
      const response = await api.get(`/bulk-import/template/${entity}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `bulk_import_${entity}_template.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch { toast.error('Failed to download template'); }
  };

  const handleUpload = async () => {
    if (!entity || !file) { toast.error('Select entity type and file'); return; }
    setUploading(true);
    setResults(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/bulk-import/${entity}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResults(res.data);
      if (res.data.imported > 0) toast.success(`${res.data.imported} of ${res.data.total} records imported`);
      if (res.data.skipped > 0) toast.error(`${res.data.skipped} records skipped — see errors below`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
      setResults({ total: 0, imported: 0, skipped: 0, errors: [{ row: '-', error: e.response?.data?.detail || 'Unknown error' }] });
    }
    setUploading(false);
  };

  const tmpl = templates[entity];

  return (
    <AdminSubPage title="Bulk Import" breadcrumb={['Admin', 'Organization', 'Bulk Import']} backPath="/dashboard/admin/organization">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 p-6 space-y-4">
            <div><Label>Entity Type *</Label>
              <Select value={entity} onValueChange={v => { setEntity(v); setFile(null); setPreview(null); setResults(null); }}>
                <SelectTrigger data-testid="bi-entity"><SelectValue placeholder="Select what to import" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ENTITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {entity && tmpl && (
              <div className="p-3 bg-blue-50 border border-blue-200 text-xs text-blue-800">
                <p className="font-semibold mb-1">{ENTITY_LABELS[entity]} CSV Format</p>
                <p className="text-[10px] mb-1">{tmpl.description}</p>
                <p><span className="font-semibold">Required columns:</span> {tmpl.required.join(', ')}</p>
                <p><span className="font-semibold">All columns:</span> {tmpl.columns.join(', ')}</p>
                <Button size="sm" variant="outline" className="mt-2 text-xs h-7 gap-1" onClick={handleDownloadTemplate} data-testid="download-template">
                  <Download className="w-3 h-3" />Download Template CSV
                </Button>
              </div>
            )}

            <div>
              <Label>Upload CSV File *</Label>
              <div className="mt-1 border-2 border-dashed border-gray-200 p-6 text-center hover:border-[#C5A059] transition-colors">
                <input type="file" accept=".csv" onChange={handleFileChange} className="hidden" id="bulk-file" data-testid="bi-file-input" />
                <label htmlFor="bulk-file" className="cursor-pointer">
                  <FileSpreadsheet className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">{file ? file.name : 'Click to select CSV file'}</p>
                  <p className="text-xs text-gray-400 mt-1">UTF-8 encoded CSV with headers in row 1</p>
                </label>
              </div>
            </div>

            {/* Preview */}
            {preview && (
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Preview ({preview.totalRows} data rows)</h3>
                <div className="border border-gray-200 overflow-x-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-gray-50 border-b">
                      {preview.headers.map((h, i) => <th key={i} className="text-left px-3 py-1.5 font-semibold text-gray-500 whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {preview.rows.map((row, ri) => (
                        <tr key={ri} className="border-b">{row.map((c, ci) => <td key={ci} className="px-3 py-1 text-gray-600 whitespace-nowrap">{c}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Button onClick={handleUpload} disabled={!entity || !file || uploading} className="bg-[#1f1f1f] text-white w-full" data-testid="bi-upload">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : <><Upload className="w-4 h-4 mr-2" />Import {file ? `(${preview?.totalRows || '?'} rows)` : ''}</>}
            </Button>
          </div>

          {/* Results */}
          {results && (
            <div className={`border p-4 ${results.imported > 0 && results.skipped === 0 ? 'bg-green-50 border-green-200' : results.skipped > 0 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="import-results">
              <div className="flex items-center gap-2 mb-2">
                {results.skipped === 0 ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertCircle className="w-5 h-5 text-amber-600" />}
                <h3 className="font-semibold text-sm">Import Results</h3>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm mb-2">
                <div><span className="text-gray-500">Total:</span> <span className="font-bold">{results.total}</span></div>
                <div><span className="text-gray-500">Imported:</span> <span className="font-bold text-green-700">{results.imported}</span></div>
                <div><span className="text-gray-500">Skipped:</span> <span className="font-bold text-red-600">{results.skipped}</span></div>
              </div>
              {results.errors?.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b"><th className="text-left px-2 py-1">Row</th><th className="text-left px-2 py-1">Error</th></tr></thead>
                    <tbody>{results.errors.map((e, i) => <tr key={i} className="border-b"><td className="px-2 py-1 font-mono">{e.row}</td><td className="px-2 py-1 text-red-700">{e.error}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right panel — Quick Guide */}
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-[#1f1f1f] mb-3">How It Works</h3>
            <ol className="text-xs text-gray-600 space-y-2 list-decimal list-inside">
              <li>Select the entity type you want to import</li>
              <li>Download the template CSV to see required columns</li>
              <li>Fill in your data (keep row 1 as headers)</li>
              <li>Upload the completed CSV</li>
              <li>Review the preview and click Import</li>
              <li>Check results — fix errors and re-upload if needed</li>
            </ol>
          </div>
          <div className="bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800">
            <p className="font-semibold mb-1">Tips</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Clients use SA ID number as the unique key</li>
              <li>Loans/Savings reference clients by <code>client_id_number</code> (SA ID)</li>
              <li>Imported loans start in "pending" status</li>
              <li>Journal entries require maker-checker approval</li>
              <li>Duplicate ID numbers are automatically skipped</li>
              <li>Save as UTF-8 CSV (not Excel format)</li>
            </ul>
          </div>
        </div>
      </div>
    </AdminSubPage>
  );
};
export default BulkImportPage;
